import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import blog from "./BlogJSON/consulJSON.json";
import BlogSideBarRight from "./BlogSideBarRight";
import ScrollButton from "../../ScrollButton";
import ProfilePic from "../../images/profile/profile-pic.jpeg";
import ConsulPic from "./blogImage/consul.png";

/**
 * ConsulBlog — Professional blog layout
 * - Hero header with metadata and share buttons
 * - Left sticky Author card + On This Page
 * - Clean readable typography
 * - Inline Q&A (no collapses)
 * - Featured diagram/image with caption
 * - Right blog sidebar preserved
 */

// naive reading time (~200 wpm)
const readingTime = (text) => Math.max(2, Math.round(text.split(/\s+/).length / 200));

export default function ConsulBlog() {
  const allText = useMemo(() => {
    const mcqText = (blog.mcqs || [])
      .map((q) => `${q.question} ${q?.answer?.brief ?? ""} ${(q?.answer?.bullets ?? []).join(" ")}`)
      .join(" ");
    return `${blog.title} ${blog.introduction} ${mcqText} ${blog.conclusion ?? ""}`;
  }, []);

  const minutes = readingTime(allText);

  const share = (network) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(blog.title);
    const via = "TechVentureLLC";
    const map = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}&via=${via}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    const win = window.open(map[network], "_blank", "noopener,noreferrer");
    if (win) win.focus();
  };

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: Author + TOC */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            {/* Author */}
            <div className="card shadow-sm border-0 mb-3 text-center">
              <div className="card-body">
                <img src={ProfilePic} className="rounded-circle mb-2" alt="Author" style={{ width: 80, height: 80, objectFit: "cover" }} />
                <h3 className="h6 mb-1">Rabin Pantha</h3>
                <p className="small text-muted mb-2">Sr. Software Engineer, Walgreens</p>
                <a className="btn btn-outline-primary btn-sm" href="https://www.linkedin.com/in/rabinpantha" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              </div>
            </div>

            {/* On This Page */}
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
                <nav className="nav flex-column small">
                  <a className="nav-link px-0" href="#intro">Introduction</a>
                  <a className="nav-link px-0" href="#mcqs">Concepts & Q&A</a>
                  <a className="nav-link px-0" href="#diagram">Architecture</a>
                  <a className="nav-link px-0" href="#conclusion">Conclusion</a>
                </nav>
              </div>
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          {/* Hero */}
          <header className="mb-3">
            <h1 className="h2 mb-2">{blog.title}</h1>
            <div className="d-flex flex-wrap align-items-center gap-3 text-muted small">
              <span>{minutes} min read</span>
              <span>•</span>
              <span>Updated recently</span>
              <span className="ms-auto d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => share("linkedin")} aria-label="Share on LinkedIn">Share ↗</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => share("twitter")} aria-label="Share on X (Twitter)">Tweet</button>
              </span>
            </div>
          </header>

          {/* Intro */}
          <section id="intro" className="mb-4">
            <p className="lead">{blog.introduction}</p>
            <div className="row g-3">
              <div className="col-12">
                <div className="card shadow-sm border-0">
                  <div className="card-body">
                    <h3 className="h6 text-uppercase text-muted mb-2">Core building blocks</h3>
                    <ul className="mb-0">
                      <li><strong>Agents</strong> (client/server) register services, run health checks, gossip membership.</li>
                      <li><strong>Catalog</strong> stores service/node metadata; <strong>health</strong> tracks check status.</li>
                      <li><strong>KV</strong> for configuration/feature flags; supports watch patterns.</li>
                      <li><strong>Connect</strong> provides service mesh with built‑in <strong>mTLS</strong> and <em>intentions</em> (L7 allow/deny).</li>
                      <li><strong>ACLs</strong> secure access to catalog, KV, intentions, and more.</li>
                      <li><strong>DNS/HTTP API</strong> for discovery from any runtime, not just sidecars.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Architecture / Diagram */}
          <section id="diagram" className="mb-4">
            <figure className="figure w-100">
              <img src={ConsulPic} className="figure-img img-fluid rounded shadow-sm" alt="Consul components overview" />
              <figcaption className="figure-caption">Consul at a glance: agents, catalog, KV, health checks, and Connect (mTLS) service mesh.</figcaption>
            </figure>
          </section>

          {/* Q&A */}
          <section id="mcqs" className="mb-4">
            <h2 className="h5 mb-3">Concepts & Q&A</h2>
            <ol className="ps-3">
              {(blog.mcqs || []).map((mcq) => (
                <li key={mcq.id} className="mb-3">
                  <h3 className="h6 mb-1">{mcq.question}</h3>
                  {mcq?.answer?.brief && <p className="mb-2">{mcq.answer.brief}</p>}
                  {Array.isArray(mcq?.answer?.bullets) && mcq.answer.bullets.length > 0 && (
                    <ul>
                      {mcq.answer.bullets.map((bullet, idx) => (
                        <li key={idx} className="mb-1">{bullet}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
            <div className="alert alert-secondary small mb-0" role="note">
              Tip: Start with DNS discovery + health checks; add Connect sidecars for zero‑trust and intentions when you need service‑to‑service authorization.
            </div>
          </section>

          {/* Conclusion */}
          {blog.conclusion && (
            <section id="conclusion" className="mb-5">
              <h2 className="h5 mb-2">Conclusion</h2>
              <p className="mb-0">{blog.conclusion}</p>
            </section>
          )}

          <div className="d-flex align-items-center gap-2 small text-muted border-top pt-3">
            <span>Was this helpful?</span>
            <Link to="/contact" className="btn btn-sm btn-outline-primary">Suggest an edit</Link>
          </div>

          <ScrollButton />
        </main>

        {/* RIGHT: Sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <BlogSideBarRight />
        </aside>
      </div>
    </div>
  );
}
